// React/Redux
import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { browserHistory} from 'react-router';

// External Libraries
import UrlParse from 'url-parse';

// Helpers
import config from 'config';
import * as mixpanelTracker from 'app-lib/mixpanel/mixpanelTracker';
import * as detectBrowser from 'app-lib/helpers/browser-detect-helper';
import * as userHelper from 'app-lib/helpers/user-helper';
import * as navigationHelper from 'app-lib/helpers/navigation-helper';
import * as launchDarkly from 'app-lib/launch-darkly/launch-darkly';
import * as searchLaunchDarkly from 'app-lib/launch-darkly/search/search-launch-darkly';

// Components
import { OverlaySpinner } from 'components/SharedComponents/overlaySpinner';

// Actions
import * as identityInfoActions from '@getgo/webinar.com-frontend-redux/lib/shared/actions/identity-info';
import * as AuthInfo from '@getgo/webinar.com-frontend-redux/lib/web/actions/auth-info';
import * as termsOfServiceActions from '@getgo/webinar.com-frontend-redux/lib/shared/actions/terms-of-service';
import * as channelActions from '@getgo/webinar.com-frontend-redux/lib/shared/actions/channel-profile';
import * as launchDarklyActions from '@getgo/webinar.com-frontend-redux/lib/web/actions/launch-darkly';


// Constants
const tosAccepted = encodeURI('?tos=true');

export class App extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            showSpinner: false,
            needsAuth: false,
            isUserLoggedIn: false,
            termsOfServiceAccepted: false
        };
    }

    componentWillMount() {
        mixpanelTracker.reportG2sAnonymousId();
        mixpanelTracker.setSuperProperties({
            aliasValue: null,
            aliasDomain: null
        });
        mixpanelTracker.setIsChannelPublic();
        detectBrowser.checkNameAndVersion();
        launchDarkly.init(null,
             [searchLaunchDarkly.subscribeForFeatureSearch.bind(null, [this.onSearchFeatureChange.bind(this)]), this.onSearchFeatureChange.bind(this)]);
        const url = new UrlParse(window.location.href, true);

        if (url.query.tos) {
            this.setState({termsOfServiceAccepted: true});
        }
        const login = url.query.login;
        const searchQuery = (url.query.q) ? `&q=${encodeURIComponent(url.query.q)}` : '';
        if (login === 'corporate') {
            this.performCorporateLogin();
        } else if (login === 'facebook') {
            this.performSocialLogin(config.socialSignIn.facebook, searchQuery);
        } else if (login === 'linkedin') {
            this.performSocialLogin(config.socialSignIn.linkedin, searchQuery);
        } else if (login === 'google') {
            this.performSocialLogin(config.socialSignIn.google, searchQuery);
        } else {
            try {
                const needsAuth = this.checkForAuth();
                if (!needsAuth) {
                    mixpanelTracker.setUserType(userHelper.getUserType(this.props.identity));
                    mixpanelTracker.reportUserSessionId();
                }
                this.setState({showSpinner: needsAuth, needsAuth});
            } catch (e) {
                console.log('error', e);
            }
        }
    }

    componentDidMount() {
        if (this.props.auth && this.props.auth.isLoggedIn) {
            this.props.getMeInfo();
        } else if (this.state.needsAuth) {
            const url = `${window.location.pathname}${encodeURIComponent(window.location.search)}`;
            this.props.startAuth(url, this.state.termsOfServiceAccepted);
        }
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.identity.me && (nextProps.identity.me !== this.props.identity.me) && (nextProps.identity.isFetching <= 0)) {
            this.performIdentityActions(nextProps.identity)
                .then(this.performTermsOfServiceActions())
                .then(this.redirectUser(nextProps.identity));
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.auth && (this.props.auth !== prevProps.auth) && this.props.auth.isLoggedIn) {
            this.props.getMeInfo();
        }
    }

    updateState() {
        this.setState({showSpinner: false, isUserLoggedIn: true});
    }

    redirectUser(identity) {
        let redirectUri = decodeURIComponent(this.props.auth.authInfo.state.pathname);
        (redirectUri.includes('edit') && identity.me.id) ? redirectUri = redirectUri.replace('edit', identity.me.id) : null;
        browserHistory.push(redirectUri);
    }

    performIdentityActions(identity) {
        return userHelper.validateIdentity(identity)
            .then(this.setMixpanelProperties(identity))
            .then(this.identifyLaunchDarkly(identity))
            .then(() => {
                if (!identity.hasEntitlements) {
                    return;
                }
                this.props.getMyChannel();
            })
            .then(this.updateState.bind(this))
            .catch(userHelper.userIdentityFailed)
            .catch(navigationHelper.navigateToInternalErrorPage);
    }

    identifyLaunchDarkly(identity) {
        const user = {
            key: identity.me.id
        };
        launchDarkly.identify(user);
    }

    onSearchFeatureChange() {
        this.props.enableSearch(searchLaunchDarkly.isSearchEnabled());
    }

    setMixpanelProperties(identity) {
        mixpanelTracker.setUserIdentity(identity);
        mixpanelTracker.setUserType(userHelper.getUserType(identity));
        mixpanelTracker.reportUserSessionId();
    }


    performTermsOfServiceActions() {
        if (this.props.auth.authInfo.state.termsOfServiceAccepted === 'true') {
            // See whether the user has already accepted the TOS
            return this.props.getTermsOfServiceAcceptanceStatus().then(() => {
                if (!this.props.termsOfService.requiredVersionAccepted) {
                    this.props.acceptTermsOfService();
                }
            });
        }
        // See whether the user has accepted the TOS
        return this.props.getTermsOfServiceAcceptanceStatus(true);
    }

    performCorporateLogin() {
        this.setState({showSpinner: true, needsAuth: true});
    }

    performSocialLogin(socialBaseUrl, searchQuery) {
        this.setState({showSpinner: true});
        const redirectUrl = `${config.app.baseUrl}${this.props.location.pathname}${tosAccepted}${searchQuery}`;
        window.location.href = `${socialBaseUrl}${encodeURIComponent(redirectUrl)}`;
    }

    checkForAuth() {
        let isLoggedIn;
        try {
            isLoggedIn = localStorage.getItem('isLoggedIn');
        } catch (e) {
            console.log('error', e);
        }
        return (
            ((isLoggedIn === 'true') ||
            window.location.href.includes('edit') ||
            window.location.href.includes('#access_token') ||
            window.location.href.includes('ticket')) &&
            config.oauth.enabled
        );
    }

    renderSpinner() {
        return <OverlaySpinner />;
    }

    renderChildren() {
        return (
            <div className="app">
                {this.props.children && React.cloneElement(this.props.children, {
                    auth: this.props.auth,
                    identity: this.props.identity,
                    channel: this.props.channel,
                    launchDarkly: this.props.launchDarkly,
                    isUserLoggedIn: this.state.isUserLoggedIn,
                    isOrganizer: userHelper.isWDCEnabledUser(this.props.identity),

                })}
            </div>
        );
    }

    render() {
        return (this.state.showSpinner) ? this.renderSpinner() : this.renderChildren();
    }
}

App.propTypes = {
    children: PropTypes.element,
    getMeInfo: PropTypes.func.isRequired,
    auth: PropTypes.object,
    identity: PropTypes.object,
    channel: PropTypes.object,
    location: PropTypes.object,
    termsOfService: PropTypes.object,
    launchDarkly: PropTypes.object,
    startAuth: PropTypes.func,
    acceptTermsOfService: PropTypes.func,
    getTermsOfServiceAcceptanceStatus: PropTypes.func,
    getMyChannel: PropTypes.func,
    enableSearch: PropTypes.func
};

export default connect(
    state => ({
        auth: state.auth,
        identity: state.identity,
        termsOfService: state.termsOfService,
        channel: state.channel,
        launchDarkly: state.launchDarkly
    }),
    dispatch => bindActionCreators(Object.assign({}, identityInfoActions, AuthInfo, termsOfServiceActions, channelActions, launchDarklyActions), dispatch)
)(App);



// WEBPACK FOOTER //
// ./src/App.js